import React, { Fragment } from "react";
import { Transition } from "@headlessui/react";
import * as RAlertDialog from "@radix-ui/react-alert-dialog";
import * as RDialog from "@radix-ui/react-dialog";
import { twMerge } from "tailwind-merge";
import { Drawer } from "@components/Drawer/Drawer";
import { useIsMobile } from "@hooks/useIsMobile";
import { type useModal } from "@hooks/useModal";

export interface IModalPrimitiveProps {
  modal: ReturnType<typeof useModal>;
  children?: React.ReactNode;
  className?: string;
  displayDrawerHandle?: boolean;
  titleId?: string;
  descId?: string;
}

export const ModalPrimitive: React.FC<IModalPrimitiveProps> = (props) => {
  const { modal, children, className, titleId, descId } = props;

  const { close, state, remove } = modal;
  const isMobile = useIsMobile();

  if (modal.asDrawer && isMobile) {
    return <Drawer {...props}>{children}</Drawer>;
  }

  const DialogPrimitive = modal.important ? RAlertDialog : RDialog;

  const ContentWrapper = modal.important ? DialogPrimitive.Overlay : "div";

  return (
    <DialogPrimitive.Root defaultOpen open={state.open} modal={false}>
      <DialogPrimitive.Portal forceMount>
        <Transition
          appear
          show={state.open}
          as={Fragment}
          afterLeave={() => {
            modal.onClose?.();
            remove();
          }}
        >
          <div className="relative z-50">
            <div
              className={twMerge(
                "fixed inset-0",
                !modal.important && "md:overflow-y-auto",
              )}
            >
              <div className="relative flex min-h-full items-center justify-center text-center md:p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div
                    className="pointer-events-auto fixed inset-0 bg-base-black/40 backdrop-blur-[6px] transition-opacity"
                    onClick={(e) => {
                      if (modal.important) {
                        e.preventDefault();
                        return;
                      }
                      modal.onCustomClose?.();
                      close();
                    }}
                  />
                </Transition.Child>
                <Transition.Child
                  as={ContentWrapper}
                  className={twMerge(
                    "fixed inset-0 grid h-full transform-gpu grid-cols-1 place-items-start justify-center overflow-y-auto will-change-transform md:place-items-center",
                    !modal.important && "md:pointer-events-none",
                  )}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <DialogPrimitive.Content
                    className={twMerge(
                      "flex min-h-[100dvh] w-full max-w-full transform flex-col overflow-hidden rounded-none bg-base-white text-left align-middle shadow-xl transition-all md:my-auto md:min-h-fit md:max-w-md md:rounded-xl",
                      !modal.important && "md:pointer-events-auto",
                      className,
                    )}
                    onEscapeKeyDown={(e) => {
                      if (modal.important ?? modal.onCustomClose) {
                        e.preventDefault();
                        modal.onCustomClose?.();
                        return;
                      }
                      close();
                    }}
                    aria-labelledby={titleId ?? ""}
                    aria-describedby={descId ?? ""}
                  >
                    {children}
                  </DialogPrimitive.Content>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Transition>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
